import { useState } from 'react';
import {
  getTechnologyIconUrl,
  getTechnologySymbolMeta,
} from '@/utils/technologySymbols';
import { cn } from '@/lib/utils';

interface TechnologySymbolProps {
  technology: string;
  size?: 'sm' | 'md';
}

const logoSizes = {
  sm: { shell: 'h-6 w-6', icon: 'h-3.5 w-3.5' },
  md: { shell: 'h-7 w-7', icon: 'h-4 w-4' },
} as const;

const fallbackSizes = {
  sm: 'h-6 min-w-[1.5rem] px-1 text-[0.55rem]',
  md: 'h-7 min-w-[1.75rem] px-1.5 text-[0.6rem]',
} as const;

export default function TechnologySymbol({
  technology,
  size = 'md',
}: TechnologySymbolProps) {
  const meta = getTechnologySymbolMeta(technology);
  const [iconFailed, setIconFailed] = useState(false);
  const dimensions = logoSizes[size];

  if (meta.iconSlug && !iconFailed) {
    return (
      <span
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full border border-[var(--inv-border-soft)] bg-[var(--inv-surface)]',
          dimensions.shell,
        )}
        title={meta.label}
        aria-label={meta.label}>
        <img
          src={getTechnologyIconUrl(meta.iconSlug)}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setIconFailed(true)}
          className={cn(dimensions.icon, 'object-contain')}
        />
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full border border-[var(--inv-border-soft)] bg-[var(--inv-surface)] font-bold leading-none tracking-tight',
        fallbackSizes[size],
        meta.fallbackClass,
      )}
      title={meta.label}
      aria-label={meta.label}>
      {meta.symbol}
    </span>
  );
}
